import { useEffect, useMemo, useState } from 'react';
import { listCertificates } from '../../p2p/certificateStore.js';
import { calculateMatchPoints } from '../../p2p/heuristics.js';
import {
  DEFAULT_RATING,
  displayRating,
  replayCertificateRatings,
  timeControlClass
} from '../../p2p/ratings.js';
import {
  ratingBucketForCertificate,
  STANDARD_TIME_CLASSES
} from '../../p2p/leaderboardBuckets.js';
import { VARIANTS } from '../../chess/variants.js';

const STANDARD_RATING_CLASSES = STANDARD_TIME_CLASSES;

export default function VerifiedHistory({ playerId }) {
  const [certificates, setCertificates] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    listCertificates({ playerId })
      .then(setCertificates)
      .catch(setError);
  }, [playerId]);

  const ratingBook = useMemo(
    () => replayCertificateRatings(certificates),
    [certificates]
  );
  const ratings = useMemo(
    () => {
      const playerRatings = ratingBook[playerId] || {};
      const standard = Object.fromEntries(
        STANDARD_RATING_CLASSES.map((ratingClass) => [
          ratingClass,
          playerRatings[ratingClass] || { ...DEFAULT_RATING }
        ])
      );
      return { ...standard, ...playerRatings };
    },
    [playerId, ratingBook]
  );
  const points = useMemo(
    () =>
      certificates.reduce((total, certificate) => {
        const opponent = certificate.players.find((player) => player.playerId !== playerId);
        const ratingBucket = ratingBucketForCertificate(certificate);
        return total + calculateMatchPoints({
          certificate,
          playerId,
          playerRating: ratings[ratingBucket]?.rating,
          opponentRating:
            ratingBook[opponent.playerId]?.[ratingBucket]?.rating
            ?? DEFAULT_RATING.rating
        }).points;
      }, 0),
    [certificates, playerId, ratingBook, ratings]
  );

  const variantBuckets = useMemo(
    () => Object.keys(ratings).filter((bucket) => bucket.includes(':')),
    [ratings]
  );

  return (
    <section className="panel verified-history">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Verified history</p>
          <h2>Ratings and certificates</h2>
        </div>
        <span className="verified-badge">✓ Local verification</span>
      </div>

      {error ? (
        <p className="error">{error.message}</p>
      ) : (
        <>
          <div className="rating-grid">
            {STANDARD_RATING_CLASSES.map((ratingClass) => (
              <div key={ratingClass}>
                <span>{ratingClass}</span>
                <strong>{displayRating(ratings[ratingClass])}</strong>
                <small>
                  RD {Math.round(ratings[ratingClass].deviation)} · {ratings[ratingClass].games} games
                </small>
              </div>
            ))}
            {variantBuckets.map((bucket) => {
              const [variantId, timeClass] = bucket.split(':');
              return (
                <div key={bucket}>
                  <span>{VARIANTS[variantId]?.label || variantId} · {timeClass}</span>
                  <strong>{displayRating(ratings[bucket])}</strong>
                  <small>
                    RD {Math.round(ratings[bucket].deviation)} · {ratings[bucket].games} games
                  </small>
                </div>
              );
            })}
            <div>
              <span>points</span>
              <strong>{Math.round(points)}</strong>
              <small>Verified rated games</small>
            </div>
          </div>

          <ol className="certificate-list">
            {certificates.length === 0 && (
              <li className="empty">
                Finished dual-signed games will appear here.
              </li>
            )}
            {[...certificates].reverse().map((certificate) => {
              let ladderClass = '';
              try {
                ladderClass = timeControlClass(certificate.timeControl);
              } catch {
                ladderClass = '';
              }
              return (
                <li key={certificate.certificateHash}>
                  <span className={`result-token result-${certificate.result}`}>
                    {certificate.result}
                  </span>
                  <span>
                    <strong>
                      <span className={certificate.rated ? 'cert-rated' : 'cert-unrated'}>
                        {certificate.rated ? 'Rated' : 'Unrated'}
                      </span>
                      {' · '}
                      {(certificate.variant && certificate.variant !== 'standard'
                        ? `${VARIANTS[certificate.variant]?.label || certificate.variant} · `
                        : '')}
                      {certificate.timeControl}
                      {ladderClass ? ` (${ladderClass})` : ''}
                      {' · '}
                      {certificate.endReason}
                    </strong>
                    <small>
                      {new Date(certificate.endTs).toLocaleString()}
                      {certificate.rated
                        ? ' · counts for rating and points'
                        : ' · casual — rating and points unchanged'}
                    </small>
                  </span>
                  <code title={certificate.certificateHash}>
                    {certificate.certificateHash.slice(0, 10)}…
                  </code>
                  <details className="proof-details">
                    <summary>Inspect verification proof</summary>
                    <dl>
                      <div>
                        <dt>Signatures</dt>
                        <dd>{Object.keys(certificate.signatures).length}/2 valid</dd>
                      </div>
                      <div>
                        <dt>Chain links</dt>
                        <dd>
                          {Object.values(certificate.chainPrev).filter(Boolean).length}/2 previous
                        </dd>
                      </div>
                      <div>
                        <dt>Time-weave</dt>
                        <dd>
                          {certificate.rated
                            ? `${Object.values(certificate.vdfProofs)
                              .reduce((sum, proof) => sum + (proof?.checkpoints?.length || 0), 0)} checkpoints`
                            : 'Not required for unrated game'}
                        </dd>
                      </div>
                    </dl>
                  </details>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </section>
  );
}
